import { useCallback, useMemo, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import type { JSONContent } from '@tiptap/core';
import type {
  PolicyAcknowledgementBlock,
  PolicyAcknowledgementTitleLevel,
  PolicyType,
} from '@course-authoring/shared-types';
import { sanitizeRichTextContent, useTheme } from '@course-authoring/shared-types';
import { createContentBlockStyles, useIsMobileViewport } from '@course-authoring/ui-blocks';
import { InteractionRichTextField } from './InteractionRichTextField';
import { createTokenRichTextToolbarStyles } from './interactionEditorUtils';
import { PdfUploadField } from './PdfUploadField';
import { TitleLevelSelector, getTitleInputStyle } from './TitleLevelSelector';

type FieldName = 'title' | 'description' | 'policyConfig';

export interface PolicyAcknowledgementEditorProps {
  block: PolicyAcknowledgementBlock;
  onChange: (updates: Partial<PolicyAcknowledgementBlock>) => void;
  autoFocus?: boolean;
  translationMode?: boolean;
}

export function PolicyAcknowledgementEditor({
  block,
  onChange,
  autoFocus = false,
  translationMode = false,
}: PolicyAcknowledgementEditorProps): React.ReactElement {
  const { tokens } = useTheme();
  const isMobile = useIsMobileViewport(tokens.layout.mobileBreakpoint);
  const baseStyles = useMemo(() => createContentBlockStyles(tokens), [tokens]);
  const containerRef = useRef<HTMLDivElement>(null);
  const [focusedField, setFocusedField] = useState<FieldName | null>(null);
  const [showTypeChangeConfirm, setShowTypeChangeConfirm] = useState(false);
  const [pendingPolicyType, setPendingPolicyType] = useState<PolicyType | null>(null);

  const titleLevel = block.titleLevel ?? 'h2';
  const toolbarStyles = useMemo(() => createTokenRichTextToolbarStyles(tokens), [tokens]);
  const sanitizedDescription = useMemo(
    () =>
      typeof block.description === 'string'
        ? { type: 'doc' as const, content: [{ type: 'paragraph' as const }] }
        : sanitizeRichTextContent(block.description as JSONContent),
    [block.description]
  );

  const handleTitleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange({ title: e.target.value });
    },
    [onChange]
  );

  const handlePolicyTypeChange = useCallback(
    (policyType: PolicyType) => {
      if (policyType === block.policyType) return;
      if (block.policyUrl) {
        setPendingPolicyType(policyType);
        setShowTypeChangeConfirm(true);
      } else {
        onChange({ policyType, policyUrl: '', pdfHasLinks: undefined });
      }
    },
    [onChange, block.policyType, block.policyUrl]
  );

  const handleConfirmTypeChange = useCallback(() => {
    if (pendingPolicyType) {
      onChange({ policyType: pendingPolicyType, policyUrl: '', pdfHasLinks: undefined });
    }
    setShowTypeChangeConfirm(false);
    setPendingPolicyType(null);
  }, [onChange, pendingPolicyType]);

  const handleCancelTypeChange = useCallback(() => {
    setShowTypeChangeConfirm(false);
    setPendingPolicyType(null);
  }, []);

  const urlError = useMemo<string | null>(() => {
    if (block.policyType !== 'link') return null;
    const url = block.policyUrl;
    if (!url.trim()) return null;
    try {
      const parsed = new URL(url);
      if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
        return 'URL must start with http:// or https://';
      }
      if (!/^[a-z0-9-]+(\.[a-z0-9-]+)*\.[a-z]{2,}$/i.test(parsed.hostname)) {
        return 'Please enter a valid URL (e.g. https://example.com/policy)';
      }
      return null;
    } catch {
      return 'Please enter a valid URL (e.g. https://example.com/policy)';
    }
  }, [block.policyType, block.policyUrl]);

  const handlePolicyUrlChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange({ policyUrl: e.target.value });
    },
    [onChange]
  );

  const handlePdfUpload = useCallback(
    ({ url, hasLinks }: { url: string; hasLinks: boolean }) => {
      onChange({ policyUrl: url, pdfHasLinks: hasLinks });
    },
    [onChange]
  );

  const isFocused = focusedField !== null;

  const styles: Record<string, CSSProperties> = {
    container: {
      display: 'flex',
      flexDirection: 'column',
      gap: tokens.spacing.lg,
      width: '100%',
      fontFamily: tokens.typography.fontFamily,
    },
    section: {
      display: 'flex',
      flexDirection: 'column',
      gap: tokens.spacing.sm,
    },
    label: {
      fontSize: '13px',
      fontWeight: 600,
      color: tokens.text.muted,
      fontFamily: tokens.typography.fontFamily,
      textTransform: 'uppercase' as const,
      letterSpacing: '0.5px',
    },
    titleInput: getTitleInputStyle(titleLevel, tokens),
    descriptionWrapper: {
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: focusedField === 'description'
        ? tokens.ui.selectionBorder
        : tokens.borders.default,
      borderRadius: tokens.radius.sm,
      padding: tokens.spacing.md,
      transition: `border-color ${tokens.ui.transitionDefault}`,
    },
    toolbar: {
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
      padding: tokens.spacing.sm,
      backgroundColor: tokens.backgrounds.app,
      border: `1px solid ${tokens.borders.default}`,
      borderRadius: tokens.radius.sm,
      boxShadow: tokens.shadow.sm,
      marginBottom: tokens.spacing.sm,
    },
    policyTypeRow: {
      display: 'flex',
      gap: tokens.spacing.sm,
    },
    policyTypeButton: {
      padding: `${tokens.spacing.xs} ${tokens.spacing.md}`,
      fontSize: '14px',
      fontWeight: 500,
      fontFamily: tokens.typography.fontFamily,
      border: `1px solid ${tokens.borders.default}`,
      borderRadius: tokens.radius.sm,
      cursor: 'pointer',
      transition: `all ${tokens.ui.transitionDefault}`,
      backgroundColor: tokens.backgrounds.app,
      color: tokens.text.primary,
    },
    policyTypeButtonActive: {
      backgroundColor: tokens.brand.primary,
      color: tokens.text.onBrand,
      border: `1px solid ${tokens.brand.primary}`,
    },
    urlInput: {
      fontSize: '14px',
      color: tokens.text.primary,
      fontFamily: tokens.typography.fontFamily,
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: urlError ? tokens.ui.errorBorder : tokens.borders.default,
      borderRadius: tokens.radius.sm,
      padding: `${tokens.spacing.sm} ${tokens.spacing.md}`,
      width: '100%',
      boxSizing: 'border-box' as const,
      outline: 'none',
    },
    urlError: {
      fontSize: '12px',
      color: tokens.ui.errorText,
      fontFamily: tokens.typography.fontFamily,
      margin: `${tokens.spacing.xs} 0 0 0`,
    },
    confirmOverlay: {
      position: 'fixed' as const,
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.4)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
    },
    confirmDialog: {
      backgroundColor: tokens.backgrounds.surface,
      borderRadius: tokens.radius.lg,
      padding: tokens.spacing["2xl"],
      maxWidth: '420px',
      width: '90%',
      boxShadow: tokens.shadow.lg,
      fontFamily: tokens.typography.fontFamily,
    },
    confirmTitle: {
      fontSize: '16px',
      fontWeight: 600,
      color: tokens.text.primary,
      margin: '0 0 8px 0',
    },
    confirmMessage: {
      fontSize: '14px',
      color: tokens.text.muted,
      lineHeight: '1.5',
      margin: '0 0 20px 0',
    },
    confirmButtonRow: {
      display: 'flex',
      justifyContent: 'flex-end',
      gap: tokens.spacing.sm,
    },
    confirmCancelButton: {
      padding: `${tokens.spacing.sm} ${tokens.spacing.lg}`,
      fontSize: '14px',
      fontWeight: 500,
      fontFamily: tokens.typography.fontFamily,
      border: `1px solid ${tokens.borders.default}`,
      borderRadius: tokens.radius.sm,
      cursor: 'pointer',
      backgroundColor: tokens.backgrounds.app,
      color: tokens.text.primary,
    },
    confirmConfirmButton: {
      padding: `${tokens.spacing.sm} ${tokens.spacing.lg}`,
      fontSize: '14px',
      fontWeight: 500,
      fontFamily: tokens.typography.fontFamily,
      border: 'none',
      borderRadius: tokens.radius.sm,
      cursor: 'pointer',
      backgroundColor: tokens.ui.errorText,
      color: '#FFFFFF',
    },
  };

  return (
    <div ref={containerRef} style={styles.container}>
      <div style={styles.section}>
        {!translationMode ? <TitleLevelSelector
          currentLevel={titleLevel}
          onSelectLevel={(level) => onChange({ titleLevel: level as PolicyAcknowledgementTitleLevel })}
          tokens={tokens}
        /> : null}
        <input
          type="text"
          value={block.title}
          onChange={handleTitleChange}
          onFocus={() => setFocusedField('title')}
          onBlur={() => setFocusedField(null)}
          placeholder="Enter policy title..."
          style={styles.titleInput}
          autoFocus={autoFocus}
        />
      </div>

      <div style={styles.section}>
        <label style={styles.label}>Description</label>
        <div style={styles.descriptionWrapper}>
          <InteractionRichTextField
            content={sanitizedDescription}
            placeholder="Enter description..."
            isFocused={focusedField === 'description'}
            onFocus={() => setFocusedField('description')}
            onBlur={() => setFocusedField(null)}
            onContentChange={(content) => onChange({ description: content })}
            autoFocus={false}
            containerRef={containerRef}
            toolbarPreset="full"
            toolbarStyles={toolbarStyles}
            editorInlineStyle={`outline: none; min-height: ${isMobile ? '40px' : '60px'};`}
            shouldRerenderOnTransaction
          />
        </div>
      </div>

      {translationMode && block.policyUrl ? (
        <div style={styles.section}>
          <span style={styles.label}>Policy document</span>
          <a href={block.policyUrl} target="_blank" rel="noreferrer">
            Open source policy
          </a>
        </div>
      ) : null}

      {!translationMode ? <><div style={styles.section}>
        <label style={styles.label}>Policy Type</label>
        <div style={styles.policyTypeRow}>
          <button
            type="button"
            style={{
              ...styles.policyTypeButton,
              ...(block.policyType === 'pdf'
                ? styles.policyTypeButtonActive
                : {}),
            }}
            onClick={() => handlePolicyTypeChange('pdf')}
          >
            PDF
          </button>
          <button
            type="button"
            style={{
              ...styles.policyTypeButton,
              ...(block.policyType === 'link'
                ? styles.policyTypeButtonActive
                : {}),
            }}
            onClick={() => handlePolicyTypeChange('link')}
          >
            Link
          </button>
        </div>
      </div>

      <div style={styles.section}>
        <label style={styles.label}>
          {block.policyType === 'pdf' ? 'PDF Document' : 'Policy URL'}
        </label>
        {block.policyType === 'pdf' ? (
          <PdfUploadField
            policyUrl={block.policyUrl}
            onUpload={handlePdfUpload}
          />
        ) : (
          <>
            <input
              type="url"
              value={block.policyUrl}
              onChange={handlePolicyUrlChange}
              onFocus={() => setFocusedField('policyConfig')}
              onBlur={() => setFocusedField(null)}
              placeholder="https://example.com/policy"
              style={styles.urlInput}
            />
            {urlError && (
              <span style={styles.urlError}>{urlError}</span>
            )}
          </>
        )}
      </div></> : null}

      {!translationMode && showTypeChangeConfirm && (
        <div
          style={styles.confirmOverlay}
          onClick={handleCancelTypeChange}
        >
          <div
            style={styles.confirmDialog}
            onClick={(e) => e.stopPropagation()}
          >
            <p style={styles.confirmTitle}>Change Policy Type?</p>
            <p style={styles.confirmMessage}>
              Changing the policy type will remove the currently saved{' '}
              {block.policyType === 'pdf' ? 'PDF document' : 'policy link'}.
              This action cannot be undone.
            </p>
            <div style={styles.confirmButtonRow}>
              <button
                type="button"
                style={styles.confirmCancelButton}
                onClick={handleCancelTypeChange}
              >
                Cancel
              </button>
              <button
                type="button"
                style={styles.confirmConfirmButton}
                onClick={handleConfirmTypeChange}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

PolicyAcknowledgementEditor.displayName = 'PolicyAcknowledgementEditor';
